import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import InfoCard from "@/components/ui/info-card";
import {
  DoubleCheck,
  Book,
  Percentage,
  ClipboardCheck,
} from "@/components/icons";
import PendingExamsList from "../components/PendingExamsList";
import OngoingTrainingsList from "../components/OngoingTrainingsList";
import ClientUpcomingTrainingsTable from "../components/ClientUpcomingTrainingsTable";
import DashboardAnnouncementsList from "@/features/dashboard/components/DashboardAnnouncementsList";
import UserPointsWidget from "@/features/user-points/components/UserPointsWidget";
import { clientDashboardApi } from "../services/client-dashboard.api";
import type {
  ClientDashboardStats,
  PendingExam,
  OngoingTraining,
  ClientDashboardUpcomingEducation,
} from "../types/client-dashboard.types";
import styles from "./ClientDashboardPage.module.scss";

const ClientDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<ClientDashboardStats | null>(null);
  const [pendingExams, setPendingExams] = useState<PendingExam[]>([]);
  const [ongoingTrainings, setOngoingTrainings] = useState<OngoingTraining[]>(
    [],
  );
  const [upcomingEducations, setUpcomingEducations] = useState<
    ClientDashboardUpcomingEducation[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    clientDashboardApi
      .getDashboard()
      .then((mapped) => {
        if (import.meta.env.DEV) {
          console.log("[client-dashboard] mapped data:", mapped);
        }
        setStats(mapped.stats);
        setPendingExams(mapped.pendingExams);
        setOngoingTrainings(mapped.ongoingTrainings);
        setUpcomingEducations(mapped.upcomingEducations);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) {
    return (
      <DashboardLayout>
        <Header title={t("clientDashboard.title")} />
        <div className={styles.pageContainer}>
          <div className={styles.infoCardGrid}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={styles.statCardSkeleton} />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const infoCards = [
    {
      key: "completedTrainings",
      title: t("clientDashboard.stats.completedTrainings"),
      value: `${stats.completedTrainings.value}`,
      icon: <DoubleCheck width={24} height={24} color="#fff" />,
      iconBgColor: "#22c55e",
      target: "/dashboard/client-educations?tab=completed",
    },
    {
      key: "ongoingTrainings",
      title: t("clientDashboard.stats.ongoingTrainings"),
      value: `${stats.ongoingTrainings.value}`,
      icon: <Book width={24} height={24} color="#fff" />,
      iconBgColor: "#3b82f6",
      target: "/dashboard/client-educations",
    },
    {
      key: "completedExams",
      title: t("clientDashboard.stats.completedExams"),
      value: `${stats.completedExams.score}/${stats.completedExams.total}`,
      icon: <ClipboardCheck width={24} height={24} color="#fff" />,
      iconBgColor: "#8b5cf6",
      target: "/dashboard/client-exams",
    },
    {
      key: "examSuccessRate",
      title: t("clientDashboard.stats.examSuccessRate"),
      value: `%${stats.examSuccessRate}`,
      icon: <Percentage width={24} height={24} color="#fff" />,
      iconBgColor: "#f59e0b",
      target: "/dashboard/client-exams",
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("clientDashboard.title")} />

      <div className={styles.pageContainer}>
        <DashboardAnnouncementsList />
        <div className={styles.infoCardGrid}>
          {infoCards.map(({ key, title, value, icon, iconBgColor, target }) => (
            <InfoCard
              key={key}
              title={title}
              value={value}
              icon={icon}
              iconBgColor={iconBgColor}
              onClick={target ? () => navigate(target) : undefined}
            />
          ))}
        </div>

        <UserPointsWidget />

        <div className={styles.middleRow}>
          <PendingExamsList items={pendingExams} />
          <OngoingTrainingsList items={ongoingTrainings} />
        </div>

        <div className={styles.tablesRow}>
          <ClientUpcomingTrainingsTable items={upcomingEducations} />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClientDashboardPage;
